// Training insights' data and the pure layout behind their charts (spec 006), as the server's /me endpoints give them. This file imports only
// the catalogs (spec 013 FR-010), so its tests run under node without the browser's globals.
import { t } from '../i18n/index.ts';

// SeriesPoint is one point along an activity: d meters and t moving seconds
// from the start, v pace in s/km or speed in m/s, and elevation and heart
// rate where known.
export interface SeriesPoint {
  d: number;
  t: number;
  v?: number;
  ele?: number;
  hr?: number;
  lat: number;
  lon: number;
}

// Series is an activity's charts (006 FR-041): runs are the stretches between
// pauses and gaps, drawn with breaks; by time charts a timer-only activity's
// heart rate over t.
export interface Series {
  pace: boolean;
  by: 'distance' | 'time';
  runs: SeriesPoint[][];
}

export interface Zones {
  seconds: number[];
  max_hr: number;
  estimated: boolean;
}

export interface RankedEffort {
  distance: string;
  seconds: number;
  rank: number;
}

export interface RecordActivity {
  activity_id: string;
  name: string;
  started_at: string;
  value: number;
}

export interface EffortRecord {
  key: string;
  meters: number;
  top: RecordActivity[];
  history: RecordActivity[];
}

export interface Records {
  sport: string;
  sports: string[];
  since?: string;
  efforts: EffortRecord[];
  longest?: RecordActivity;
  moving?: RecordActivity;
  climb?: RecordActivity;
}

export interface LoadDay {
  date: string;
  load: number;
  estimated?: boolean;
  fitness: number;
  fatigue: number;
  form: number;
  resting_hr?: number;
  hrv?: number;
  hrv_kind?: 'sdnn' | 'rmssd';
}

export interface Heatmap {
  routes: { sport: string; lines: string[] }[];
  bounds?: [number, number, number, number];
  sports: string[];
}

export type CalendarMeasure = 'steps' | 'moving' | 'sleep';

export interface YearCalendar {
  year: number;
  measure: CalendarMeasure;
  days: { date: string; value: number }[];
  max: number;
}

// effortName is how a best effort's distance reads, "1 mile" or "5K".
export function effortName(key: string): string {
  return t('trainingEffort', { effort: key });
}

// effortBadge says where an effort ranks: "Best 5K" or "2nd fastest 1 km".
export function effortBadge(e: Pick<RankedEffort, 'distance' | 'rank'>): string {
  return t('trainingEffortBadge', { effort: effortName(e.distance), rank: e.rank });
}

// decodePolyline reads a Google encoded polyline at precision 5 into
// [lon, lat] pairs, the order MapLibre draws.
export function decodePolyline(s: string): [number, number][] {
  const out: [number, number][] = [];
  let i = 0, lat = 0, lon = 0;
  const next = (): number => {
    let shift = 0, result = 0, b: number;
    do {
      b = s.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20 && i < s.length);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < s.length) {
    lat += next();
    if (i >= s.length) break;
    lon += next();
    out.push([lon / 1e5, lat / 1e5]);
  }
  return out;
}

// iso writes a local date as YYYY-MM-DD.
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// yearGrid lays a year out as week columns of 7 weekday rows, the first row
// being weekStart (0 Sunday). Cells before January 1 and after December 31
// are null, so the first and last columns may be partial.
export function yearGrid(year: number, weekStart: number): (string | null)[][] {
  const first = new Date(year, 0, 1, 12);
  const lead = (first.getDay() - weekStart + 7) % 7;
  const cols: (string | null)[][] = [];
  const d = new Date(year, 0, 1 - lead, 12);
  while (d.getFullYear() <= year) {
    const col: (string | null)[] = [];
    for (let r = 0; r < 7; r++) {
      col.push(d.getFullYear() === year ? iso(d) : null);
      d.setDate(d.getDate() + 1);
    }
    cols.push(col);
  }
  return cols;
}

// shade is a calendar square's step: -1 without data, 0 for none, then 1
// to 4 by quarters of the year's highest.
export function shade(value: number | undefined, max: number): number {
  if (value == null) return -1;
  if (value <= 0 || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((4 * value) / max)));
}

// hrZone is the heart rate zone a reading falls in, 0 to 4 for zones 1 to 5
// of max bounded at 50, 60, 70, 80, and 90% as the server counts them,
// readings over the maximum in zone 5, and -1 below 50%. The small nudge
// keeps a reading at exactly 60% in zone 2 despite floating point.
export function hrZone(hr: number, max: number): number {
  const tenths = Math.floor((hr / max) * 10 + 1e-9);
  return tenths < 5 ? -1 : Math.min(4, tenths - 5);
}

// FORM_BANDS are form's ranges (spec 010 FR-033), highest first: fresh
// above +5, neutral from −10 to +5, productive from −30 to −10, and
// overreaching below −30.
export const FORM_BANDS: { name: string; lo: number; hi: number }[] = (
  [
    ['fresh', 5, Infinity],
    ['neutral', -10, 5],
    ['productive', -30, -10],
    ['overreaching', -Infinity, -30],
  ] as const
).map(([band, lo, hi]) => ({
  lo,
  hi,
  get name() {
    return t('trainingFormBand', { band });
  },
}));

// formBand names the band a day's form is in; a value on a boundary belongs
// to the band nearer neutral.
export function formBand(form: number): string {
  if (form > 5) return t('trainingFormBand', { band: 'fresh' });
  if (form >= -10) return t('trainingFormBand', { band: 'neutral' });
  if (form >= -30) return t('trainingFormBand', { band: 'productive' });
  return t('trainingFormBand', { band: 'overreaching' });
}
